import {
  createElement,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';

/**
 * L'INFOBULLE DE L'APPLICATION, une seule pour tout (2026-11-04, Georges :
 * « harmoniser les infobulles »). Jusque-la trois recettes coexistaient :
 * le `title` natif du navigateur (gris, lent, au style de l'OS), la bulle
 * sombre du bouton « ? » (`AideEnBulle`) et celle de la synthese de paie.
 * Toutes passent par ici.
 *
 * Ce qu'elle fait :
 *   - au survol ou au focus de ce qu'elle enveloppe, une bulle sombre
 *     apparait AU-DESSUS, centree ; en dessous s'il n'y a pas la place ;
 *   - la bulle est rendue dans un PORTAIL, en position fixe : un tableau
 *     qui defile ou une carte en `overflow-hidden` ne la rogne jamais —
 *     c'est ce qui obligeait la synthese de paie a ouvrir la sienne vers
 *     le haut ;
 *   - elle se ferme au defilement, a Echap et quand le pointeur sort.
 *
 * Ce qu'elle ne fait pas : parler au lecteur d'ecran. Le texte d'une
 * icone seule reste dans son `aria-label`, comme avant.
 *
 * `balise` choisit l'element enveloppant quand un `span` ne convient pas
 * (une cellule, une ligne de liste, un bloc qui tronque). Sans `texte`,
 * l'enveloppe est rendue telle quelle, sans bulle.
 */
export function Infobulle({
  texte,
  children,
  position = 'haut',
  large = false,
  balise = 'span',
  className,
  style,
  onClick,
}: {
  texte: ReactNode | null | undefined;
  children?: ReactNode;
  /** Cote prefere ; la bulle change de cote s'il manque la place. */
  position?: 'haut' | 'bas';
  /** Un texte long, sur plusieurs lignes (aide d'un ecran). */
  large?: boolean;
  balise?: 'span' | 'div' | 'li' | 'td' | 'label';
  className?: string;
  style?: CSSProperties;
  /** Pour une enveloppe qui est elle-meme cliquable (une ligne de liste). */
  onClick?: () => void;
}) {
  const ancre = useRef<HTMLElement | null>(null);
  const bulle = useRef<HTMLDivElement | null>(null);
  const [ouverte, setOuverte] = useState(false);
  const [place, setPlace] = useState<{ x: number; y: number; sens: 'haut' | 'bas' } | null>(null);

  const fermer = useCallback(() => setOuverte(false), []);

  // Au defilement ou au redimensionnement, la bulle serait a cote de son
  // ancre : on la ferme, elle revient au prochain survol.
  useEffect(() => {
    if (!ouverte) return;
    const auClavier = (e: KeyboardEvent) => {
      if (e.key === 'Escape') fermer();
    };
    window.addEventListener('scroll', fermer, true);
    window.addEventListener('resize', fermer);
    document.addEventListener('keydown', auClavier);
    return () => {
      window.removeEventListener('scroll', fermer, true);
      window.removeEventListener('resize', fermer);
      document.removeEventListener('keydown', auClavier);
    };
  }, [ouverte, fermer]);

  // La position se calcule une fois la bulle rendue, pour connaitre sa
  // taille : au-dessus de l'ancre si elle y tient, sinon en dessous, et
  // jamais hors de l'ecran a gauche ou a droite.
  useLayoutEffect(() => {
    if (!ouverte || !ancre.current || !bulle.current) return;
    const a = ancre.current.getBoundingClientRect();
    const b = bulle.current.getBoundingClientRect();
    const marge = 6;
    const tientEnHaut = a.top - b.height - marge >= 4;
    const tientEnBas = a.bottom + b.height + marge <= window.innerHeight - 4;
    const sens = position === 'haut' ? (tientEnHaut || !tientEnBas ? 'haut' : 'bas') : tientEnBas || !tientEnHaut ? 'bas' : 'haut';
    const centre = a.left + a.width / 2;
    const x = Math.min(Math.max(centre, b.width / 2 + 4), window.innerWidth - b.width / 2 - 4);
    const y = sens === 'haut' ? a.top - marge : a.bottom + marge;
    setPlace((courant) =>
      courant && courant.x === x && courant.y === y && courant.sens === sens ? courant : { x, y, sens },
    );
  }, [ouverte, position, texte]);

  if (texte === null || texte === undefined || texte === '') {
    return createElement(balise, { className, style, onClick }, children);
  }

  const enveloppe = createElement(
    balise,
    {
      ref: ancre,
      className,
      style,
      onClick,
      onMouseEnter: () => setOuverte(true),
      onMouseLeave: fermer,
      onFocus: () => setOuverte(true),
      onBlur: fermer,
    },
    children,
  );

  return (
    <>
      {enveloppe}
      {ouverte &&
        createPortal(
          <div
            ref={bulle}
            role="tooltip"
            className={`pointer-events-none fixed z-50 rounded-md bg-encre-900 px-2 py-1 text-xs font-normal leading-relaxed text-white shadow-lg ${
              large ? 'w-80 whitespace-normal px-3 py-2' : 'max-w-sm whitespace-pre-line'
            }`}
            style={{
              left: place?.x ?? -9999,
              top: place?.y ?? -9999,
              transform: `translate(-50%, ${place?.sens === 'bas' ? '0' : '-100%'})`,
              visibility: place ? 'visible' : 'hidden',
            }}
          >
            {texte}
          </div>,
          document.body,
        )}
    </>
  );
}
